/**
 * The asset library's routes (ACP-D51; docs/gfx-materials/plan.md §4), spread into `GRAPHICS_ROUTES`: the tenant's
 * library items (materials, images, environments, flipbooks) and the built-in copy. Cloud only like the rest of the
 * graphics library until M2, tenant-wide permissions — `graphics.read` reads, `graphics.edit` writes (as themes). The
 * staff tenant (D39) is refused every write (`staffTenant.ts`' `graphics` tag).
 */
import {
  deleteGraphicsLibraryItemResponseWireSchema,
  graphicsLibraryItemFromBuiltinRequestWireSchema,
  graphicsLibraryItemFromBuiltinResponseWireSchema,
  graphicsLibraryItemPathWireSchema,
  graphicsLibraryItemResponseWireSchema,
  listGraphicsLibraryItemsQueryWireSchema,
  listGraphicsLibraryItemsResponseWireSchema,
  revertGraphicsLibraryItemRequestWireSchema,
  saveGraphicsLibraryItemRequestWireSchema,
  saveGraphicsLibraryItemResponseWireSchema,
} from './graphicsLibraryItems.js';
import type { RouteDef } from './routes.js';

const MISSING_CONTENT =
  '`validation-failed` when a content object it names (the item’s document, an asset, the preview) is not in the tenant’s content store — upload it first (`POST /graphics/content/uploads`) — or an asset’s size is not its object’s; `issues` names each one.';

export const GRAPHICS_LIBRARY_ITEM_ROUTES = {
  listGraphicsLibraryItems: {
    id: 'listGraphicsLibraryItems',
    method: 'GET',
    path: '/graphics/library/items',
    tag: 'graphics',
    summary: 'List library items',
    description:
      'The tenant’s asset library (ACP-D51): every live item — of one `kind` when the query names it — with its current revision (its preview’s hash among it), by name. Deleted ones are left out. Content objects are fetched through `POST /graphics/content/downloads`.',
    params: null,
    query: listGraphicsLibraryItemsQueryWireSchema,
    body: null,
    response: listGraphicsLibraryItemsResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
  getGraphicsLibraryItem: {
    id: 'getGraphicsLibraryItem',
    method: 'GET',
    path: '/graphics/library/items/{itemId}',
    tag: 'graphics',
    summary: 'Get a library item',
    description: 'The item, every revision of it (newest first), and a presigned GET (valid five minutes) of each content object its current revision names: its document, its assets and its preview. `not-found` for a deleted one.',
    params: graphicsLibraryItemPathWireSchema,
    query: null,
    body: null,
    response: graphicsLibraryItemResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  saveGraphicsLibraryItem: {
    id: 'saveGraphicsLibraryItem',
    method: 'PUT',
    path: '/graphics/library/items/{itemId}',
    tag: 'graphics',
    summary: 'Save a library item',
    description: `Creates the item at revision 1 (\`baseRevision: null\`; \`conflict\` when a live one has the id — a deleted one is brought back at its next revision) or saves a new immutable revision from the one named (another write since is \`conflict\`; a deleted item is \`not-found\`). Its \`kind\` is set when it is created and never changes (another is \`conflict\`). Every revision is kept; a save of exactly the current revision’s name, category, tags and content objects writes nothing and answers it. ${MISSING_CONTENT}`,
    params: graphicsLibraryItemPathWireSchema,
    query: null,
    body: saveGraphicsLibraryItemRequestWireSchema,
    response: saveGraphicsLibraryItemResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  revertGraphicsLibraryItem: {
    id: 'revertGraphicsLibraryItem',
    method: 'POST',
    path: '/graphics/library/items/{itemId}/revert',
    tag: 'graphics',
    summary: 'Revert a library item',
    description:
      'A new revision equal to an older one (`revertTo`): its content objects, name, category, tags and origin. From the current revision named (`baseRevision`; another write since is `conflict`). `not-found` for a deleted item or a revision it never had; `validation-failed` when `revertTo` is the current revision.',
    params: graphicsLibraryItemPathWireSchema,
    query: null,
    body: revertGraphicsLibraryItemRequestWireSchema,
    response: saveGraphicsLibraryItemResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  graphicsLibraryItemFromBuiltin: {
    id: 'graphicsLibraryItemFromBuiltin',
    method: 'POST',
    path: '/graphics/library/items/from-builtin',
    tag: 'graphics',
    summary: 'Copy a built-in into the library',
    description:
      'Copies a built-in of the stage’s catalog (`builtinId`, at `revision` or, null, the newest the catalog has) into the tenant’s library: each content object the tenant lacks (the document, the assets, the preview) is copied server-side from the catalog into the tenant’s content, then the item is written under the id `builtinId` with each dot an underscore, of the built-in’s kind, with `origin` set. Idempotent per `builtinId@revision`: when the copy’s current revision is that built-in revision’s content, nothing is written (`outcome: existing`). A copy of an older revision — or of this one, since edited or reverted by the tenant — is updated with a new revision of the built-in’s content (`updated`; its earlier revisions, the tenant’s edits among them, stay and can be reverted to); a deleted copy is brought back (`created`). `not-found` for a built-in or revision the catalog does not have (or of a kind this server does not know); `conflict` when the library holds a newer revision of it, or an item of its own under that id; `unavailable` when the stage has no catalog.',
    params: null,
    query: null,
    body: graphicsLibraryItemFromBuiltinRequestWireSchema,
    response: graphicsLibraryItemFromBuiltinResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  deleteGraphicsLibraryItem: {
    id: 'deleteGraphicsLibraryItem',
    method: 'DELETE',
    path: '/graphics/library/items/{itemId}',
    tag: 'graphics',
    summary: 'Delete a library item',
    description:
      'Soft-deletes the item: it leaves the library and reads `not-found`; its revisions and content stay (a scene’s linked copy holds its own content and becomes a local one at its next sync). Saving under its id with `baseRevision: null` (and its kind) brings it back at its next revision.',
    params: graphicsLibraryItemPathWireSchema,
    query: null,
    body: null,
    response: deleteGraphicsLibraryItemResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
} as const satisfies Readonly<Record<string, RouteDef>>;
